import { z } from 'zod'

import type { AiChatBotTool, LegacyToolDefinition } from '../types'

/**
 * Wraps a legacy ToolDefinition (from @justrelate/chatbot) into a AiChatBotTool.
 * All existing tools are read-only so concurrencySafe defaults to true.
 */
export function wrapLegacyTool<T>(
  tool: LegacyToolDefinition<T>,
): AiChatBotTool<T> {
  return {
    name: tool.name,
    description: tool.description,
    concurrencySafe: true,
    cacheable: true,

    toOpenAI() {
      return {
        type: 'function' as const,
        function: {
          name: tool.name,
          description: tool.description,
          parameters: z.toJSONSchema(tool.parameters) as Record<
            string,
            unknown
          >,
        },
      }
    },

    async run(rawArgs: T): Promise<unknown> {
      // `as T`: same contravariance workaround as tool-system.ts — schema erased to unknown at call site.
      const parsed = tool.parameters.parse(rawArgs) as T
      return tool.run(parsed)
    },
  }
}

/**
 * Auto-detects and normalizes tools: AiChatBotTool passes through, LegacyToolDefinition is wrapped.
 */
export function normalizeTools(
  tools: (LegacyToolDefinition<unknown> | AiChatBotTool<unknown>)[],
): AiChatBotTool<unknown>[] {
  return tools.map((t) => ('toOpenAI' in t ? t : wrapLegacyTool(t)))
}
